import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TableChartIcon from "@mui/icons-material/TableChart";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

interface Props {
  open: boolean;
  previewUrl: string | null;
  fileName: string;
  fullPath: string;
  onClose: () => void;
  onDownload: (fullPath: string, fileName: string) => void;
  onOpenInNewTab: (fullPath: string) => void;
}

const getFileType = (fileName: string): "pdf" | "image" | "html" | "excel" | "other" => {
  const ext = fileName.split(".").pop()?.toLowerCase() || "";
  if (ext === "pdf") return "pdf";
  if (ext === "html" || ext === "htm") return "html";
  if (["xlsx", "xls", "xlsm", "xlsb"].includes(ext)) return "excel";
  if (["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(ext)) return "image";
  return "other";
};

const ContactFilePreviewDialog: React.FC<Props> = ({
  open,
  previewUrl,
  fileName,
  fullPath,
  onClose,
  onDownload,
  onOpenInNewTab,
}) => {
  const fileType = getFileType(fileName);

  const renderContent = () => {
    if (!previewUrl) return null;

    if (fileType === "pdf") {
      return (
        <Box sx={{ width: "100%", height: "700px" }}>
          <iframe
            src={previewUrl}
            width="100%"
            height="100%"
            style={{ border: "none" }}
            title="תצוגת PDF"
          />
        </Box>
      );
    }

    if (fileType === "image") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
            backgroundColor: "grey.100",
            borderRadius: 1,
            p: 2,
          }}
        >
          <img
            src={previewUrl}
            alt={fileName}
            style={{ maxWidth: "100%", maxHeight: "700px", objectFit: "contain" }}
          />
        </Box>
      );
    }

    if (fileType === "html") {
      return (
        <Box sx={{ width: "100%", height: "700px" }}>
          <iframe
            src={previewUrl}
            width="100%"
            height="100%"
            sandbox="allow-same-origin"
            style={{ border: "1px solid #e0e0e0", borderRadius: "4px" }}
            title="תצוגת HTML"
          />
        </Box>
      );
    }

    if (fileType === "excel") {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "400px",
            gap: 3,
            p: 4,
          }}
        >
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: 2,
              backgroundColor: "success.50",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "2px solid",
              borderColor: "success.200",
            }}
          >
            <TableChartIcon sx={{ fontSize: 80, color: "success.main" }} />
          </Box>
          <Typography variant="h5" color="text.primary" fontWeight="medium">
            קובץ Excel
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ maxWidth: 500 }}>
            {fileName}
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 500 }}>
            הורד את הקובץ כדי לפתוח אותו ב-Excel או ביישום גיליונות אלקטרוניים אחר
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => onDownload(fullPath, fileName)}
            startIcon={<DownloadIcon />}
            sx={{ "& .MuiButton-startIcon": { marginRight: 1, marginLeft: 0 } }}
          >
            הורד ופתח ב-Excel
          </Button>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "300px",
          gap: 2,
        }}
      >
        <InsertDriveFileIcon sx={{ fontSize: 80, color: "grey.400" }} />
        <Typography variant="h6" color="text.secondary">
          לא ניתן להציג תצוגה מקדימה לסוג קובץ זה
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {fileName}
        </Typography>
        <Button variant="contained" onClick={() => onDownload(fullPath, fileName)} startIcon={<DownloadIcon />}>
          הורד קובץ
        </Button>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth dir="rtl">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">תצוגה מקדימה - {fileName}</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>{renderContent()}</DialogContent>
      <DialogActions sx={{ p: 2, gap: 1, justifyContent: "space-between" }}>
        <Button onClick={onClose} variant="outlined">
          סגור
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={() => onDownload(fullPath, fileName)}
            variant="outlined"
            startIcon={<DownloadIcon />}
            sx={{ "& .MuiButton-startIcon": { marginRight: 1, marginLeft: 0 } }}
          >
            הורד
          </Button>
          <Button
            onClick={() => onOpenInNewTab(fullPath)}
            variant="contained"
            startIcon={<OpenInNewIcon />}
            sx={{ "& .MuiButton-startIcon": { marginRight: 1, marginLeft: 0 } }}
          >
            פתח בטאב חדש
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ContactFilePreviewDialog;
